import React from 'react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mobile } from 'helpers/utils/screensizes';
import { clamp, getWindowStart } from '../helpers';

type PaginationDotsProps = {
  totalPositions?: number;
  total?: number;
  active: number;
  maxVisibleDots?: number;
  slidesPerView?: number;
  activeDotColor?: string;
  isPDPImageModal?: string;
  ffid?:string;
  dotSize?: number;
  alwaysRender?: boolean;
};

export default function PaginationDots({
  totalPositions,
  total,
  active,
  maxVisibleDots = 4,
  slidesPerView = 1,
  activeDotColor = '#7A7A7A',
  isPDPImageModal,
  ffid,
  dotSize,
  alwaysRender = false,
}: Readonly<PaginationDotsProps>) {
  let safeTotal = 0;

  // We are either given the total number of positions directly, or we
  // calculate from the number of slides and the number of slides per view.
  // Also check actual numbers to make sure we need to display dots.
  if (totalPositions && totalPositions > 1) {
    safeTotal = totalPositions;
  } else if (total && slidesPerView && total > 1 && total > slidesPerView) {
    safeTotal = Math.max(0, total - Math.floor(slidesPerView) + 1);
  }

  const [isMobile] = useMediaQuery(mobile);
  const safeActive = clamp(active, 0, Math.max(safeTotal - 1, 0));

  const visibleCount = Math.min(safeTotal, maxVisibleDots);
  const start = React.useMemo(
    () => getWindowStart(safeActive, safeTotal, maxVisibleDots),
    [safeActive, safeTotal, maxVisibleDots],
  );

  if (!safeTotal && !alwaysRender) return null;

  dotSize ??= isMobile ? 8 : 7;
  const dotSizeActive = dotSize * 2.5;
  const stepPx = dotSize + (isMobile ? 12 : 8);

  return (
    <div
      className="overflow-hidden"
      style={{
        width: (visibleCount - 1) * stepPx + dotSizeActive,
        height: dotSize
      }}
      data-ffid={`ctr-carousel-pagination${ffid?'-'+ffid:''}`}
    >
      <div
        className="h-full flex items-center transition-transform duration-300 ease-out gap-8 sm:gap-12"
        style={{ transform: `translateX(-${start * stepPx}px)` }}
        data-ffid={`ctr-carousel-pagination-track${ffid?'-'+ffid:''}`}
      >
        {Array.from({ length: safeTotal }).map((_, i) => {
          const isActive = i === safeActive;

          return (
            <span
              key={'colorDots'+i}
              className="shrink-0 rounded-full transition-all duration-300 ease-out"
              style={{
                backgroundColor: isActive ? activeDotColor : isPDPImageModal ?? '#7A7A7A',
                width: isActive ? dotSizeActive : dotSize,
                height: dotSize,
              }}
              data-ffid={`lbl-carousel-pagination-dot-${ffid ?? ''}${i}`}
            />
          );
        })}
      </div>
    </div>
  );
}
